import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import FactionBlockedState from '../components/faction/FactionBlockedState';
import EditFactionFormWrestler from '../components/faction/EditFactionFormWrestler';
import InjuryIcon from '../components/InjuryIcon';
import WrestlerAvailabilityList from '../components/roster/WrestlerAvailabilityList';
import CreditIcon from '../components/CreditIcon';
import PremiumFeaturePreview from '../components/premium/PremiumFeaturePreview';
import {
  PremiumFeaturePill,
  PremiumInfoButton,
} from '../components/premium/PremiumInfoButton';
import { usePremium } from '../premium/PremiumContext';
import {
  buildFactionMemberFields,
  getFactionStoredWrestlers,
  getFactionWrestlerIds,
  mergeFactionWrestlersWithDetails,
} from '../utils/factionWrestlers';
import {
  buildFactionLineupUpdateAfterSwap,
  buildSwapReplacementMap,
} from '../utils/factionLineup';
import { getFactionMemberSwapSummary } from '../utils/factionScoring';
import {
  buildSwapRequestPairings,
  getSwapRequestAllWrestlerIds,
  getSwapRequestPreviewLabel,
  getSwapRequestWrestlerIds,
} from '../utils/swapRequests';
import {
  buildPremiumRemovalSuggestions,
  buildPremiumTradeSuggestions,
  loadPremiumWrestlerTrendScores,
} from '../utils/premiumWrestlerInsights';

const DEFAULT_MAX_SWAPS = 8;
const FREE_AGENT_TARGET = '__free_agents__';
const SWAP_REQUEST_MESSAGE_MAX_LENGTH = 220;
const TRUTHY_VALUES = new Set(['1', 'true', 'yes', 'si', 'y']);

const normalizeString = (value) => (value ?? '').toString().trim();
const getFactionOwnerUid = (factionData) =>
  factionData?.ownerUid || factionData?.ownerId || factionData?.userId || factionData?.uid || '';

const toBoolean = (value, fallback = false) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    return TRUTHY_VALUES.has(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const normalizeBrand = (brand) => {
  const normalized = normalizeString(brand);
  const lowerBrand = normalized.toLowerCase();

  if (lowerBrand === 'raw') return 'Raw';
  if (lowerBrand === 'smackdown' || lowerBrand === 'smack down') return 'SmackDown';
  if (lowerBrand === 'nxt') return 'NXT';

  return normalized;
};

const normalizeGender = (gender) => {
  const normalized = normalizeString(gender);
  const lowerGender = normalized.toLowerCase();

  if (['uomo', 'male', 'm', 'man'].includes(lowerGender)) return 'Uomo';
  if (['donna', 'female', 'f', 'woman'].includes(lowerGender)) return 'Donna';

  return normalized;
};

const getPremiumPositionLabel = (value) => {
  const position = Number(value) || 0;

  if (position >= 5) return 'Main Eventer';
  if (position === 4) return 'Upper';
  if (position === 3) return 'Mid';
  if (position === 2) return 'Low';
  if (position === 1) return 'Jobber';
  return 'Non TV';
};

const getPremiumPositionClassName = (value) => {
  const position = Number(value) || 0;

  if (position >= 5) return 'text-wrestlers-division-5';
  if (position === 4) return 'text-wrestlers-division-4';
  if (position === 3) return 'text-wrestlers-division-3';
  if (position === 2) return 'text-wrestlers-division-2';
  if (position === 1) return 'text-wrestlers-division-1';
  return 'text-wrestlers-division-0';
};

const getLatestWrestlerPrice = (data) => {
  const prices = Array.isArray(data?.prices) ? data.prices : [];
  const latestPrice = prices[prices.length - 1];
  const timelinePrice = Number(latestPrice?.value ?? latestPrice?.price ?? latestPrice);

  if (Number.isFinite(timelinePrice)) {
    return timelinePrice;
  }

  const directPrice = Number(data?.price ?? data?.cost ?? 0);
  return Number.isFinite(directPrice) ? directPrice : 0;
};

const matchesRosterBrandFilter = (roster, wrestler) => {
  const brand = normalizeBrand(wrestler?.brand);

  if (roster?.onlyNXT) {
    return brand === 'NXT';
  }

  if (roster?.onlyMainRoster) {
    return brand === 'Raw' || brand === 'SmackDown';
  }

  return true;
};

const buildSwapPayload = (entry, wrestlerInId, wrestlerInScore) => ({
  id: wrestlerInId,
  cost: entry.cost || 0,
  startScore: wrestlerInScore || 0,
  accumulatedPoints: 0,
  status: entry.status || null,
});

const buildSwapHistory = (wrestlerOutId, wrestlerInId, swapSummary, slotCost, operationType = 'swap') => ({
  wrestlerOut: doc(db, 'wrestlers', wrestlerOutId),
  wrestlerIn: doc(db, 'wrestlers', wrestlerInId),
  scoreOut: swapSummary.savedPoints,
  currentPointsOut: swapSummary.currentPoints,
  totalPointsOut: swapSummary.totalPoints,
  sumPointsOut: swapSummary.savedPoints,
  priceOut: slotCost || 0,
  priceIn: slotCost || 0,
  date: serverTimestamp(),
  operationType,
});

const normalizeWrestlerData = (id, data = {}) => {
  const {
    premiumComment,
    position,
    storyline,
    ...standardData
  } = data;

  return {
    ...standardData,
    id,
    name: standardData.name || standardData.nome || 'Sconosciuto',
    score: standardData.score ?? 0,
    imageUrl: standardData.imageUrl || '',
    price: getLatestWrestlerPrice(standardData),
    brand: normalizeBrand(standardData.brand),
    position: Number(position ?? 0) || 0,
    gender: normalizeGender(standardData.gender),
    active: toBoolean(standardData.active, true),
    isInjured: toBoolean(
      standardData.isInjured ?? standardData.is_injured ?? standardData.injured,
      false
    ),
  };
};

const normalizeWrestler = (snap) => normalizeWrestlerData(snap.id, snap.data());

const buildFactionMembers = (factionData, wrestlersById) =>
  mergeFactionWrestlersWithDetails(factionData, wrestlersById);

const getMinimumCount = (value) => {
  const minimum = Number(value ?? 0);
  return Number.isFinite(minimum) ? Math.max(0, minimum) : 0;
};

const countMembersByGender = (members = []) =>
  members.reduce(
    (counts, member) => {
      const gender = normalizeGender(member?.gender);

      if (gender === 'Uomo') counts.Uomo += 1;
      if (gender === 'Donna') counts.Donna += 1;

      return counts;
    },
    { Uomo: 0, Donna: 0 }
  );

const buildProjectedMembersAfterSwap = ({
  currentMembers = [],
  outgoingIds = [],
  incomingMembers = [],
}) => {
  const outgoingSet = new Set(outgoingIds.filter(Boolean));

  return [
    ...currentMembers.filter((member) => !outgoingSet.has(member.id)),
    ...incomingMembers.filter((member) => member?.id),
  ];
};

const getGenderLabel = (genderKey, count) => {
  if (genderKey === 'Uomo') {
    return count === 1 ? 'uomo' : 'uomini';
  }

  return count === 1 ? 'donna' : 'donne';
};

const getRosterGenderMinimumViolation = ({
  roster,
  members = [],
  messagePrefix = 'Non puoi completare questo scambio',
  subject = 'la tua fazione',
}) => {
  const minMale = getMinimumCount(roster?.minMaleWrestlers);
  const minFemale = getMinimumCount(roster?.minFemaleWrestlers);

  if (!minMale && !minFemale) {
    return null;
  }

  const genderCounts = countMembersByGender(members);

  if (genderCounts.Uomo < minMale) {
    return `${messagePrefix}: ${subject} scenderebbe a ${genderCounts.Uomo} ${getGenderLabel('Uomo', genderCounts.Uomo)}, ma il roster richiede almeno ${minMale} ${getGenderLabel('Uomo', minMale)}.`;
  }

  if (genderCounts.Donna < minFemale) {
    return `${messagePrefix}: ${subject} scenderebbe a ${genderCounts.Donna} ${getGenderLabel('Donna', genderCounts.Donna)}, ma il roster richiede almeno ${minFemale} ${getGenderLabel('Donna', minFemale)}.`;
  }

  return null;
};

const getFactionGenderMinimumViolationAfterSwap = ({
  roster,
  currentMembers = [],
  outgoingIds = [],
  incomingMembers = [],
  messagePrefix,
  subject,
}) =>
  getRosterGenderMinimumViolation({
    roster,
    members: buildProjectedMembersAfterSwap({
      currentMembers,
      outgoingIds,
      incomingMembers,
    }),
    messagePrefix,
    subject,
  });

const getShowBlockWindow = (showStart) => {
  if (!(showStart instanceof Date) || Number.isNaN(showStart.getTime())) return null;

  const showEnd = new Date(showStart);
  showEnd.setDate(showEnd.getDate() + 1);
  showEnd.setHours(12, 0, 0, 0);
  return { showStart, showEnd };
};

const getRequestBadge = (item) => {
  if (item.autoApproved) {
    return { className: 'bg-success', label: 'Accettata subito' };
  }

  if (item.status === 'accepted') {
    return { className: 'bg-success', label: 'Accettata' };
  }

  if (item.status === 'rejected') {
    return { className: 'bg-danger', label: 'Rifiutata' };
  }

  return { className: 'bg-warning text-dark', label: 'In attesa' };
};

export default function FactionMarketPage() {
  const { id: factionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [favoriteWrestlerIds, setFavoriteWrestlerIds] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [faction, setFaction] = useState(null);
  const [roster, setRoster] = useState(null);
  const [members, setMembers] = useState([]);
  const [allWrestlers, setAllWrestlers] = useState([]);
  const [wrestlersById, setWrestlersById] = useState({});
  const [rosterFactions, setRosterFactions] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [selectedToRemove, setSelectedToRemove] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [swapCount, setSwapCount] = useState(0);
  const [freeSwapEndDate, setFreeSwapEndDate] = useState(null);
  const [isWithinFirstDay, setIsWithinFirstDay] = useState(false);
  const [canEditMembers, setCanEditMembers] = useState(false);
  const [autoBlocked, setAutoBlocked] = useState(false);
  const [currentShow, setCurrentShow] = useState(null);
  const [swapsData, setSwapsData] = useState({ enabled: true, blockedAt: null });
  const [selectedOwnWrestlerIds, setSelectedOwnWrestlerIds] = useState([]);
  const [selectedTargetFactionId, setSelectedTargetFactionId] = useState('');
  const [selectedTargetWrestlerIds, setSelectedTargetWrestlerIds] = useState([]);
  const [swapTargetMode, setSwapTargetMode] = useState('faction');
  const [swapRequestMessage, setSwapRequestMessage] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestActionId, setRequestActionId] = useState('');
  const [premiumTradeTrendMap, setPremiumTradeTrendMap] = useState({});
  const maxSwaps = Math.max(0, Number(roster?.maxSwap ?? DEFAULT_MAX_SWAPS) || 0);
  const { isPremium } = usePremium();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadFavoriteWrestlers = async () => {
      if (!currentUser?.uid) {
        setFavoriteWrestlerIds([]);
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        setFavoriteWrestlerIds(Array.isArray(userSnap.data()?.favoriteWrestler) ? userSnap.data().favoriteWrestler : []);
      } catch (favoriteError) {
        console.error(favoriteError);
        setFavoriteWrestlerIds([]);
      }
    };

    loadFavoriteWrestlers();
  }, [currentUser?.uid]);

  const loadSwapRequests = useCallback(async (rosterId) => {
    if (!rosterId) {
      setSwapRequests([]);
      return;
    }

    const snap = await getDocs(collection(db, 'rosters', rosterId, 'swapRequest'));
    const requests = snap.docs
      .map((requestDoc) => ({ id: requestDoc.id, ...requestDoc.data() }))
      .sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));

    setSwapRequests(requests);
  }, []);

  const loadMarketData = useCallback(async () => {
    if (!factionId) return;

    setLoading(true);
    setError('');

    try {
      const factionSnap = await getDoc(doc(db, 'factions', factionId));
      if (!factionSnap.exists()) {
        setFaction(null);
        setError('Fazione non trovata');
        return;
      }

      const rawFactionData = { id: factionSnap.id, ...factionSnap.data() };
      const factionData = {
        ...rawFactionData,
        ownerUid: getFactionOwnerUid(rawFactionData),
      };
      setFaction(factionData);
      setSwapCount(factionData.swapsUsed || 0);

      let rosterData = null;
      if (factionData.rosterId) {
        const rosterSnap = await getDoc(doc(db, 'rosters', factionData.rosterId));
        if (rosterSnap.exists()) rosterData = { id: rosterSnap.id, ...rosterSnap.data() };
      } else if (factionData.roster) {
        const rosterSnap = await getDocs(query(collection(db, 'rosters'), where('name', '==', factionData.roster)));
        if (!rosterSnap.empty) rosterData = { id: rosterSnap.docs[0].id, ...rosterSnap.docs[0].data() };
      }
      setRoster(rosterData);

      const wrestlerSnaps = await getDocs(collection(db, 'wrestlers'));
      const wrestlerList = wrestlerSnaps.docs.map(normalizeWrestler);
      const wrestlerMap = wrestlerList.reduce((acc, wrestlerItem) => {
        acc[wrestlerItem.id] = wrestlerItem;
        return acc;
      }, {});
      setWrestlersById(wrestlerMap);

      const factionsQuery = factionData.rosterId
        ? query(collection(db, 'factions'), where('rosterId', '==', factionData.rosterId))
        : query(collection(db, 'factions'), where('roster', '==', factionData.roster));
      const rosterFactionsDocs = await getDocs(factionsQuery);
      const factionsData = rosterFactionsDocs.docs.map((fDoc) => {
        const rawFaction = { id: fDoc.id, ...fDoc.data() };
        return {
          ...rawFaction,
          ownerUid: getFactionOwnerUid(rawFaction),
        };
      });
      setRosterFactions(factionsData);

      const ownMemberIds = new Set(getFactionWrestlerIds(factionData));
      const shouldExcludeAssignedWrestlers = rosterData?.gameType !== 'Indipendente';
      const otherAssignedIds = new Set();
      factionsData.forEach((item) => {
        if (item.id === factionId) return;
        getFactionWrestlerIds(item).forEach((wrestlerId) => otherAssignedIds.add(wrestlerId));
      });

      setAllWrestlers(
        wrestlerList.filter((wrestlerItem) => {
          if (!wrestlerItem.active) return false;
          if (ownMemberIds.has(wrestlerItem.id)) return false;
          if (shouldExcludeAssignedWrestlers && otherAssignedIds.has(wrestlerItem.id)) return false;
          if (!matchesRosterBrandFilter(rosterData, wrestlerItem)) return false;
          return true;
        })
      );

      setMembers(buildFactionMembers(factionData, wrestlerMap));
      setSelectedToAdd([]);
      setSelectedToRemove([]);
      setSelectedOwnWrestlerIds([]);
      setSelectedTargetFactionId('');
      setSelectedTargetWrestlerIds([]);
      setSwapTargetMode('faction');

      if (rosterData?.id) {
        await loadSwapRequests(rosterData.id);
      } else {
        setSwapRequests([]);
      }
    } catch (loadError) {
      console.error(loadError);
      setError('Errore durante il caricamento del mercato');
    } finally {
      setLoading(false);
    }
  }, [factionId, loadSwapRequests]);

  useEffect(() => {
    loadMarketData();
  }, [loadMarketData]);

  useEffect(() => {
    if (!faction) return;

    const creationDate = faction.createdAt instanceof Date
      ? faction.createdAt
      : faction.createdAt?.seconds
      ? new Date(faction.createdAt.seconds * 1000)
      : null;
    const startDate = faction.dataInizioPunteggio instanceof Date
      ? faction.dataInizioPunteggio
      : faction.dataInizioPunteggio?.seconds
      ? new Date(faction.dataInizioPunteggio.seconds * 1000)
      : creationDate;

    setFreeSwapEndDate(startDate);

    if (!creationDate) {
      setIsWithinFirstDay(false);
      return;
    }

    const now = new Date();
    const diffHours = (now - creationDate) / (1000 * 60 * 60);
    setIsWithinFirstDay(diffHours < 24 || (startDate ? now < startDate : false));
  }, [faction]);

  useEffect(() => {
    const fetchSwapsState = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'swaps'));
        if (!snap.exists()) {
          setSwapsData({ enabled: true, blockedAt: null });
          return;
        }

        const data = snap.data();
        setSwapsData({
          enabled: data.enabled !== false,
          blockedAt: data.blockedAt ? new Date(data.blockedAt.seconds * 1000) : null,
        });
      } catch (loadError) {
        console.error(loadError);
        setSwapsData({ enabled: true, blockedAt: null });
      }
    };

    fetchSwapsState();
  }, []);

  useEffect(() => {
    const checkForActiveShow = async () => {
      try {
        const now = new Date();
        const lookbackDate = new Date(now.getTime() - 48 * 60 * 60 * 1000);
        const showsSnap = await getDocs(query(collection(db, 'shows'), where('data', '>=', lookbackDate)));
        let activeShow = null;

        showsSnap.forEach((docSnap) => {
          const showData = docSnap.data();
          const showStart = showData.data?.toDate();
          if (!showStart) return;

          const showWindow = getShowBlockWindow(showStart);
          if (!showWindow) return;

          if (now >= showWindow.showStart && now <= showWindow.showEnd) {
            activeShow = { id: docSnap.id, ...showData, data: showStart, endTime: showWindow.showEnd };
          }
        });

        setCurrentShow(activeShow);
        setAutoBlocked(Boolean(activeShow));
      } catch (loadError) {
        console.error(loadError);
      }
    };

    checkForActiveShow();
    const intervalId = setInterval(checkForActiveShow, 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!roster) return;

    setCanEditMembers(roster.gameType === 'Indipendente' && swapCount < maxSwaps && !autoBlocked && swapsData.enabled);
  }, [autoBlocked, maxSwaps, roster, swapCount, swapsData.enabled]);

  useEffect(() => {
    setSelectedTargetWrestlerIds([]);
    setSelectedTargetFactionId(swapTargetMode === 'free_agent' ? FREE_AGENT_TARGET : '');
  }, [swapTargetMode]);

  useEffect(() => {
    if (!selectedTargetFactionId) {
      setSelectedTargetWrestlerIds([]);
      return;
    }

    if (selectedTargetFactionId === FREE_AGENT_TARGET) {
      return;
    }

    const targetFaction = rosterFactions.find((item) => item.id === selectedTargetFactionId);
    const targetFactionWrestlerIds = new Set(getFactionWrestlerIds(targetFaction));

    setSelectedTargetWrestlerIds((prev) =>
      prev.filter((wrestlerId) => targetFactionWrestlerIds.has(wrestlerId))
    );
  }, [rosterFactions, selectedTargetFactionId]);

  const handleToggleRemove = (wrestlerId) => {
    setSelectedToRemove((prev) => (prev.includes(wrestlerId) ? prev.filter((id) => id !== wrestlerId) : [...prev, wrestlerId]));
    setSuccessMessage('');
    setError('');
  };

  const isCompetitive = roster?.gameType === 'Competitivo';
  const isSectionBlocked = autoBlocked || !swapsData.enabled;

  const remainingBudget = useMemo(() => {
    if (!faction) return 0;

    const creditsFromRemoved = selectedToRemove.reduce(
      (sum, wrestlerId) => sum + (members.find((item) => item.id === wrestlerId)?.cost || 0),
      0
    );
    const creditsForAdded = selectedToAdd.reduce(
      (sum, wrestlerId) => sum + (allWrestlers.find((item) => item.id === wrestlerId)?.price || 0),
      0
    );

    return faction.creditsLeft + creditsFromRemoved - creditsForAdded;
  }, [allWrestlers, faction, members, selectedToAdd, selectedToRemove]);

  const handleToggleAdd = (wrestlerId) => {
    const wrestlerItem = allWrestlers.find((item) => item.id === wrestlerId);
    if (!wrestlerItem) return;

    if (selectedToAdd.includes(wrestlerId)) {
      setSelectedToAdd((prev) => prev.filter((id) => id !== wrestlerId));
      setSuccessMessage('');
      setError('');
      return;
    }

    if (selectedToAdd.length >= selectedToRemove.length || wrestlerItem.price > remainingBudget) return;

    setSelectedToAdd((prev) => [...prev, wrestlerId]);
    setSuccessMessage('');
    setError('');
  };

  const priceDetails = useMemo(() => {
    const totalOut = selectedToRemove.reduce(
      (sum, wrestlerId) => sum + (members.find((item) => item.id === wrestlerId)?.cost || 0),
      0
    );
    const totalIn = selectedToAdd.reduce(
      (sum, wrestlerId) => sum + (allWrestlers.find((item) => item.id === wrestlerId)?.price || 0),
      0
    );

    return { totalOut, totalIn, newCredits: (faction?.creditsLeft || 0) + totalOut - totalIn };
  }, [allWrestlers, faction?.creditsLeft, members, selectedToAdd, selectedToRemove]);

  const targetFactions = useMemo(
    () => rosterFactions.filter((item) => item.id !== faction?.id && getFactionWrestlerIds(item).length > 0),
    [faction?.id, rosterFactions]
  );

  const freeAgentMembers = useMemo(() => allWrestlers, [allWrestlers]);

  const sortFavoritesFirst = useCallback((items) => (
    [...items].sort((a, b) => {
      const aFavorite = favoriteWrestlerIds.includes(a.id);
      const bFavorite = favoriteWrestlerIds.includes(b.id);

      if (aFavorite !== bFavorite) {
        return aFavorite ? -1 : 1;
      }

      return (a.name || '').localeCompare(b.name || '', 'it');
    })
  ), [favoriteWrestlerIds]);

  const sortedAllWrestlers = useMemo(
    () => sortFavoritesFirst(allWrestlers),
    [allWrestlers, sortFavoritesFirst]
  );

  const sortedFreeAgentMembers = useMemo(
    () => sortFavoritesFirst(freeAgentMembers),
    [freeAgentMembers, sortFavoritesFirst]
  );

  const premiumTradeCandidateIds = useMemo(() => {
    const ids = [
      ...members.map(member => member.id),
      ...sortedFreeAgentMembers.map(member => member.id),
      ...targetFactions.flatMap(item => getFactionWrestlerIds(item)),
    ].filter(Boolean);

    return [...new Set(ids)];
  }, [members, sortedFreeAgentMembers, targetFactions]);

  useEffect(() => {
    if (!isPremium || premiumTradeCandidateIds.length === 0) {
      setPremiumTradeTrendMap({});
      return undefined;
    }

    let ignore = false;

    loadPremiumWrestlerTrendScores({
      db,
      wrestlerIds: premiumTradeCandidateIds,
    })
      .then(nextTrendMap => {
        if (!ignore) setPremiumTradeTrendMap(nextTrendMap);
      })
      .catch(loadError => {
        console.error('Errore caricamento trend premium mercato:', loadError);
        if (!ignore) setPremiumTradeTrendMap({});
      });

    return () => {
      ignore = true;
    };
  }, [isPremium, premiumTradeCandidateIds]);

  const premiumSuggestionWrestlersById = useMemo(() => {
    if (!isPremium || Object.keys(premiumTradeTrendMap).length === 0) {
      return wrestlersById;
    }

    return Object.entries(premiumTradeTrendMap).reduce(
      (acc, [wrestlerId, trendData]) => ({
        ...acc,
        [wrestlerId]: {
          ...(acc[wrestlerId] || {}),
          ...trendData,
        },
      }),
      { ...wrestlersById }
    );
  }, [isPremium, premiumTradeTrendMap, wrestlersById]);

  const premiumSuggestionFreeAgents = useMemo(
    () => {
      if (!isPremium) return [];

      return sortedFreeAgentMembers.map(member => ({
        ...member,
        ...(premiumTradeTrendMap[member.id] || {}),
      }));
    },
    [isPremium, premiumTradeTrendMap, sortedFreeAgentMembers]
  );

  const premiumTradeSuggestions = useMemo(
    () => {
      if (!isPremium) return [];

      return buildPremiumTradeSuggestions({
        targetFactions,
        freeAgents: premiumSuggestionFreeAgents,
        wrestlersById: premiumSuggestionWrestlersById,
        favoriteWrestlerIds,
        limit: 6,
      });
    },
    [favoriteWrestlerIds, isPremium, premiumSuggestionFreeAgents, premiumSuggestionWrestlersById, targetFactions]
  );

  const premiumFactionTradeSuggestions = useMemo(
    () => {
      if (!isPremium || !isCompetitive) return [];

      return buildPremiumTradeSuggestions({
        targetFactions,
        freeAgents: [],
        wrestlersById: premiumSuggestionWrestlersById,
        favoriteWrestlerIds,
        limit: 4,
      });
    },
    [favoriteWrestlerIds, isCompetitive, isPremium, premiumSuggestionWrestlersById, targetFactions]
  );

  const premiumAuctionSuggestions = useMemo(
    () => {
      if (!isPremium || !isCompetitive) return [];

      return buildPremiumTradeSuggestions({
        targetFactions: [],
        freeAgents: premiumSuggestionFreeAgents,
        wrestlersById: premiumSuggestionWrestlersById,
        favoriteWrestlerIds,
        limit: 4,
      });
    },
    [favoriteWrestlerIds, isCompetitive, isPremium, premiumSuggestionFreeAgents, premiumSuggestionWrestlersById]
  );

  const sortedOwnMembers = useMemo(
    () => [...members].sort((a, b) => {
      if (a.isInjured !== b.isInjured) return a.isInjured ? -1 : 1;
      return a.name.localeCompare(b.name, 'it');
    }),
    [members]
  );

  const marketPreviewRemovalSuggestions = useMemo(() => {
    const previewMembers = sortedOwnMembers.slice(0, 2);

    if (previewMembers.length > 0) {
      return previewMembers.map((member) => ({
        id: member.id,
        name: member.name || 'Wrestler',
        status:
          member.active === false
            ? 'Non attivo'
            : member.isInjured
            ? 'Infortunio'
            : 'Trend nascosto',
        points: Number(member.score || 0),
      }));
    }

    return [
      { id: 'preview-out-1', name: 'Slot da valutare', status: 'Trend nascosto', points: 0 },
      { id: 'preview-out-2', name: 'Wrestler in calo', status: 'Trend nascosto', points: 0 },
    ];
  }, [sortedOwnMembers]);

  const marketPreviewTargetFaction = useMemo(
    () =>
      rosterFactions.find(
        (item) =>
          item.id !== faction?.id && getFactionWrestlerIds(item).length > 0
      ) || null,
    [faction?.id, rosterFactions]
  );

  const marketPreviewTradeSuggestions = useMemo(() => {
    const previewSource = marketPreviewTargetFaction
      ? sortFavoritesFirst(buildFactionMembers(marketPreviewTargetFaction, wrestlersById))
      : sortedFreeAgentMembers;
    const sourceName = marketPreviewTargetFaction?.name || 'Svincolati';
    const previewMembers = previewSource.slice(0, 2);

    if (previewMembers.length > 0) {
      return previewMembers.map((member) => ({
        id: member.id,
        name: member.name || 'Wrestler',
        sourceName,
        points: Number(member.score || 0),
        price: Number(member.price || 0),
      }));
    }

    return [
      { id: 'preview-in-1', name: 'Nome nascosto', sourceName, points: 0, price: 0 },
      { id: 'preview-in-2', name: 'Altro profilo premium', sourceName, points: 0, price: 0 },
    ];
  }, [
    marketPreviewTargetFaction,
    sortFavoritesFirst,
    sortedFreeAgentMembers,
    wrestlersById,
  ]);

  const marketPreviewAuctionSuggestions = useMemo(() => {
    const previewMembers = sortedFreeAgentMembers.slice(0, 2);

    if (previewMembers.length > 0) {
      return previewMembers.map((member) => ({
        id: member.id,
        name: member.name || 'Wrestler',
        points: Number(member.score || 0),
        price: Number(member.price || 0),
      }));
    }

    return [
      { id: 'preview-auction-1', name: 'Svincolato in crescita', points: 0, price: 0 },
      { id: 'preview-auction-2', name: 'Nome monitorato', points: 0, price: 0 },
    ];
  }, [sortedFreeAgentMembers]);

  const premiumRemovalSuggestions = useMemo(
    () => {
      if (!isPremium) return [];

      return buildPremiumRemovalSuggestions({
        members: sortedOwnMembers,
        trendScoresById: premiumTradeTrendMap,
        limit: 4,
      });
    },
    [isPremium, premiumTradeTrendMap, sortedOwnMembers]
  );

  const displayedPremiumTradeSuggestions = isCompetitive
    ? premiumFactionTradeSuggestions
    : premiumTradeSuggestions;

  const selectedOwnWrestlerId = selectedOwnWrestlerIds[0] || '';

  const selectedOwnMembers = useMemo(
    () =>
      selectedOwnWrestlerIds
        .map((wrestlerId) => members.find((member) => member.id === wrestlerId) || null)
        .filter(Boolean),
    [members, selectedOwnWrestlerIds]
  );

  const selectedOwnWrestler = useMemo(
    () => members.find((member) => member.id === selectedOwnWrestlerId) || null,
    [members, selectedOwnWrestlerId]
  );

  useEffect(() => {
    if (swapTargetMode === 'free_agent' && !selectedOwnWrestler?.isInjured) {
      setSwapTargetMode('faction');
    }
  }, [selectedOwnWrestler?.isInjured, swapTargetMode]);

  const isFreeAgentRequest = swapTargetMode === 'free_agent';

  const targetMembers = useMemo(() => {
    if (isFreeAgentRequest) {
      return sortedFreeAgentMembers;
    }

    const targetFaction = rosterFactions.find((item) => item.id === selectedTargetFactionId);
    return targetFaction
      ? sortFavoritesFirst(buildFactionMembers(targetFaction, wrestlersById))
      : [];
  }, [
    isFreeAgentRequest,
    rosterFactions,
    selectedTargetFactionId,
    sortFavoritesFirst,
    sortedFreeAgentMembers,
    wrestlersById
  ]);

  const selectedTargetMembers = useMemo(
    () =>
      selectedTargetWrestlerIds
        .map((wrestlerId) => targetMembers.find((member) => member.id === wrestlerId) || null)
        .filter(Boolean),
    [selectedTargetWrestlerIds, targetMembers]
  );

  const selectedTargetFactionName = useMemo(() => {
    if (isFreeAgentRequest) return 'Svincolati';

    return (
      targetFactions.find((item) => item.id === selectedTargetFactionId)?.name ||
      ''
    );
  }, [isFreeAgentRequest, selectedTargetFactionId, targetFactions]);

  const handleUsePremiumSuggestion = (suggestion) => {
    if (!suggestion?.id) return;

    if (suggestion.sourceType === 'free_agent') {
      setSwapTargetMode('free_agent');
      setSelectedTargetFactionId(FREE_AGENT_TARGET);
    } else {
      setSwapTargetMode('faction');
      setSelectedTargetFactionId(suggestion.sourceFactionId || '');
    }

    setSelectedTargetWrestlerIds([suggestion.id]);
    setSuccessMessage('');
    setError('');
  };

  const handleUsePremiumRemovalSuggestion = (suggestion) => {
    if (!suggestion?.id) return;

    setSelectedOwnWrestlerIds([suggestion.id]);
    setSuccessMessage('');
    setError('');
  };

  const renderMarketPremiumPreview = () => (
    <PremiumFeaturePreview
      previewId="faction-market-premium-preview"
      scope={faction?.id || factionId || 'global'}
      title="Anteprima mercato Premium"
      description="Trend scambi, uscite da valutare e nomi da monitorare in una card dedicata."
      overlayTitle="Aiuto scambi, proposte e consigli asta"
      overlayText="Scopri quali sono i wrestler migliori da chiedere, quali da valutare in uscita e quali da monitorare per la prossima asta."
      featureList={['Trend scambi', 'Uscite consigliate', 'Asta mirata']}
      className="mb-3"
    >
      <div className="market-choice-card premium-trade-helper premium-preview-card__mock-panel mb-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <div className="fw-bold text-light">Aiuto scambi</div>
            <PremiumFeaturePill label="Premium" />
          </div>
        </div>

        <div className="mb-3">
          <div className="premium-trade-helper__section-title">
            Da valutare in uscita
          </div>
          <div className="premium-trade-helper__list">
            {marketPreviewRemovalSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="premium-trade-helper__item premium-trade-helper__item--remove"
              >
                <span className="premium-trade-helper__main">
                  <strong>{suggestion.name}</strong>
                  <small>{suggestion.status}</small>
                </span>
                <span className="premium-trade-helper__meta">
                  <span>{suggestion.points} pt</span>
                  <span>trend</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="premium-trade-helper__section-title">
            Da chiedere
          </div>
          <div className="premium-trade-helper__list">
            {marketPreviewTradeSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="premium-trade-helper__item">
                <span className="premium-trade-helper__main">
                  <strong>{suggestion.name}</strong>
                  <small>{suggestion.sourceName}</small>
                </span>
                <span className="premium-trade-helper__meta">
                  <span>{suggestion.points} pt</span>
                  <span>
                    {suggestion.price}
                    <CreditIcon size={14} />
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {isCompetitive && (
          <div className="mt-3">
            <div className="premium-trade-helper__section-title">
              Suggerimenti prossima asta
            </div>
            <div className="premium-trade-helper__list">
              {marketPreviewAuctionSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="premium-trade-helper__item">
                  <span className="premium-trade-helper__main">
                    <strong>{suggestion.name}</strong>
                  </span>
                  <span className="premium-trade-helper__meta">
                    <span>{suggestion.points} pt</span>
                    <span>
                      {suggestion.price}
                      <CreditIcon size={14} />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PremiumFeaturePreview>
  );

  const toggleOwnWrestlerSelection = useCallback((wrestlerId) => {
    if (!wrestlerId) return;

    setSelectedOwnWrestlerIds((prev) =>
      prev.includes(wrestlerId)
        ? prev.filter((id) => id !== wrestlerId)
        : [...prev, wrestlerId]
    );
  }, []);

  const toggleTargetWrestlerSelection = useCallback((wrestlerId) => {
    if (!wrestlerId) return;

    setSelectedTargetWrestlerIds((prev) =>
      prev.includes(wrestlerId)
        ? prev.filter((id) => id !== wrestlerId)
        : [...prev, wrestlerId]
    );
  }, []);

  const factionSwapRequests = useMemo(
    () => swapRequests.filter((item) => item.requesterFactionId === faction?.id || item.targetFactionId === faction?.id),
    [faction?.id, swapRequests]
  );

  const pendingFactionRequests = useMemo(
    () => factionSwapRequests.filter((item) => item.status === 'pending'),
    [factionSwapRequests]
  );

  const incomingPendingRequests = useMemo(
    () => pendingFactionRequests.filter((item) => item.targetFactionId === faction?.id),
    [faction?.id, pendingFactionRequests]
  );

  const selectedSwapPairs = useMemo(() => {
    const pairCount = Math.max(selectedOwnMembers.length, selectedTargetMembers.length);

    return Array.from({ length: pairCount }, (_, index) => ({
      ownMember: selectedOwnMembers[index] || null,
      targetMember: selectedTargetMembers[index] || null,
    }));
  }, [selectedOwnMembers, selectedTargetMembers]);

  const hasPendingConflict = useMemo(
    () => {
      const selectedIds = new Set([
        ...selectedOwnWrestlerIds,
        ...selectedTargetWrestlerIds,
      ]);

      if (selectedIds.size === 0) return false;

      return swapRequests.some(
        (item) =>
          item.status === 'pending' &&
          getSwapRequestAllWrestlerIds(item).some((wrestlerId) =>
            selectedIds.has(wrestlerId)
          )
      );
    },
    [selectedOwnWrestlerIds, selectedTargetWrestlerIds, swapRequests]
  );

  const canAutoApproveFreeAgentSwap = Boolean(
    isCompetitive &&
    isFreeAgentRequest &&
    selectedOwnWrestler?.isInjured &&
    selectedTargetWrestlerIds.length === 1
  );

  const handleCreateSwapRequest = async () => {
    if (!roster || !faction) return;

    if (autoBlocked) {
      setError('Mercato bloccato durante lo show in corso. Le modifiche tornano disponibili alle 12:00 del giorno successivo.');
      return;
    }

    if (!swapsData.enabled) {
      setError('Mercato temporaneamente bloccato dalle impostazioni del roster.');
      return;
    }

    const requesterWrestlerIds = [...selectedOwnWrestlerIds];
    const targetWrestlerIds = [...selectedTargetWrestlerIds];
    const normalizedRequestMessage = normalizeString(swapRequestMessage)
      .slice(0, SWAP_REQUEST_MESSAGE_MAX_LENGTH);
    const targetFaction = isFreeAgentRequest
      ? null
      : rosterFactions.find((item) => item.id === selectedTargetFactionId);
    const ownWrestlers = requesterWrestlerIds
      .map((wrestlerId) => wrestlersById[wrestlerId] || null)
      .filter(Boolean);
    const targetWrestlers = targetWrestlerIds
      .map((wrestlerId) => wrestlersById[wrestlerId] || null)
      .filter(Boolean);
    const ownWrestler = ownWrestlers[0] || null;
    const targetWrestler = targetWrestlers[0] || null;

    if (
      requesterWrestlerIds.length === 0 ||
      !selectedTargetFactionId ||
      targetWrestlerIds.length === 0
    ) {
      setError('Seleziona la fazione avversaria e almeno un wrestler per lato');
      return;
    }

    if (requesterWrestlerIds.length !== ownWrestlers.length || targetWrestlerIds.length !== targetWrestlers.length) {
      setError('Uno o piu wrestler selezionati non sono piu disponibili');
      return;
    }

    if (!isFreeAgentRequest && requesterWrestlerIds.length !== targetWrestlerIds.length) {
      setError('Per inviare una richiesta multipla devi selezionare lo stesso numero di wrestler in uscita e in entrata');
      return;
    }

    if (isFreeAgentRequest && (requesterWrestlerIds.length !== 1 || targetWrestlerIds.length !== 1)) {
      setError('La sostituzione con svincolato supporta un solo wrestler per lato');
      return;
    }

    if (!ownWrestler || !targetWrestler) {
      setError('Dati richiesta non validi');
      return;
    }

    const ownFactionWrestlerIds = new Set(getFactionWrestlerIds(faction));
    if (requesterWrestlerIds.some((wrestlerId) => !ownFactionWrestlerIds.has(wrestlerId))) {
      setError('Almeno uno dei wrestler selezionati non appartiene piu alla tua fazione');
      return;
    }

    if (!isFreeAgentRequest && !targetFaction) {
      setError('La fazione selezionata non e valida');
      return;
    }

    if (
      !isFreeAgentRequest &&
      targetWrestlerIds.some(
        (wrestlerId) => !getFactionWrestlerIds(targetFaction).includes(wrestlerId)
      )
    ) {
      setError('Almeno uno dei wrestler richiesti non appartiene piu alla fazione selezionata');
      return;
    }

    if (
      isFreeAgentRequest &&
      targetWrestlerIds.some(
        (wrestlerId) => !freeAgentMembers.some((item) => item.id === wrestlerId)
      )
    ) {
      setError('Il wrestler selezionato non e piu disponibile tra gli svincolati');
      return;
    }

    const requesterGenderViolation = getFactionGenderMinimumViolationAfterSwap({
      roster,
      currentMembers: members,
      outgoingIds: requesterWrestlerIds,
      incomingMembers: targetWrestlers,
      messagePrefix: isFreeAgentRequest
        ? 'Non puoi completare questa sostituzione'
        : 'Non puoi inviare questa proposta di scambio',
      subject: 'la tua fazione',
    });
    if (requesterGenderViolation) {
      setError(requesterGenderViolation);
      return;
    }

    if (!isFreeAgentRequest) {
      const targetCurrentMembers = buildFactionMembers(targetFaction, wrestlersById);
      const targetGenderViolation = getFactionGenderMinimumViolationAfterSwap({
        roster,
        currentMembers: targetCurrentMembers,
        outgoingIds: targetWrestlerIds,
        incomingMembers: ownWrestlers,
        messagePrefix: 'Scambio non valido',
        subject: `la fazione ${targetFaction.name || 'avversaria'}`,
      });

      if (targetGenderViolation) {
        setError(targetGenderViolation);
        return;
      }
    }

    if (hasPendingConflict) {
      setError('Uno dei wrestler selezionati e gia coinvolto in una richiesta pendente');
      return;
    }

    setRequestSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      if (isFreeAgentRequest) {
        if (!selectedOwnWrestler?.isInjured) {
          setError('Puoi usare uno svincolato solo per sostituire un wrestler infortunato.');
          return;
        }

        const requesterEntry = getFactionStoredWrestlers(faction).find((item) => item.id === selectedOwnWrestlerId);
        if (!requesterEntry) {
          setError('Dati richiesta non validi');
          return;
        }

        await runTransaction(db, async (transaction) => {
          const factionRef = doc(db, 'factions', faction.id);
          const requestRef = doc(collection(db, 'rosters', roster.id, 'swapRequest'));
          const factionSnap = await transaction.get(factionRef);
          const ownWrestlerRef = doc(db, 'wrestlers', ownWrestler.id);
          const freeAgentRef = doc(db, 'wrestlers', targetWrestler.id);
          const ownWrestlerSnap = await transaction.get(ownWrestlerRef);
          const freeAgentSnap = await transaction.get(freeAgentRef);

          if (!factionSnap.exists() || !ownWrestlerSnap.exists() || !freeAgentSnap.exists()) {
            throw new Error('Impossibile completare lo scambio automatico.');
          }

          const liveFaction = factionSnap.data();
          const liveEntry = getFactionStoredWrestlers(liveFaction).find((item) => item.id === ownWrestler.id);
          if (!liveEntry) {
            throw new Error('Il wrestler infortunato non appartiene piu alla fazione.');
          }

          const liveWrestlersById = {
            ...wrestlersById,
            [ownWrestler.id]: normalizeWrestlerData(ownWrestler.id, ownWrestlerSnap.data()),
            [targetWrestler.id]: normalizeWrestlerData(targetWrestler.id, freeAgentSnap.data()),
          };
          const liveFactionMembers = buildFactionMembers(liveFaction, liveWrestlersById);
          const liveGenderViolation = getFactionGenderMinimumViolationAfterSwap({
            roster,
            currentMembers: liveFactionMembers,
            outgoingIds: [ownWrestler.id],
            incomingMembers: [liveWrestlersById[targetWrestler.id]],
            messagePrefix: 'Non puoi completare questa sostituzione',
            subject: 'la tua fazione',
          });
          if (liveGenderViolation) {
            throw new Error(liveGenderViolation);
          }

          const ownScore = ownWrestlerSnap.data().score || 0;
          const freeScore = freeAgentSnap.data().score || 0;
          const ownSwapSummary = getFactionMemberSwapSummary({
            faction: liveFaction,
            roster,
            wrestlerId: ownWrestler.id,
            currentScore: ownScore,
            startScore: liveEntry.startScore || 0,
            accumulatedPoints: liveEntry.accumulatedPoints || 0,
            status: liveEntry.status || null,
          });

          const updatedWrestlers = getFactionStoredWrestlers(liveFaction).map((item) =>
            item.id === ownWrestler.id
              ? {
                  ...item,
                  ...buildSwapPayload(item, targetWrestler.id, freeScore),
                  id: targetWrestler.id,
                  wrestlerId: targetWrestler.id,
                }
              : item
          );
          const lineupUpdate = buildFactionLineupUpdateAfterSwap({
            faction: liveFaction,
            roster,
            updatedWrestlers,
            replacements: buildSwapReplacementMap([ownWrestler.id], [targetWrestler.id]),
            now: new Date(),
          });

          transaction.update(factionRef, {
            ...buildFactionMemberFields(updatedWrestlers),
            ...(lineupUpdate || {}),
            accumulatedPoints: (liveFaction.accumulatedPoints || 0) + ownSwapSummary.currentPoints,
            sumPoints: (liveFaction.sumPoints || 0) + ownSwapSummary.savedPoints,
            updatedAt: serverTimestamp(),
          });

          transaction.set(
            doc(collection(db, 'factions', faction.id, 'swaps')),
            buildSwapHistory(
              ownWrestler.id,
              targetWrestler.id,
              ownSwapSummary,
              liveEntry.cost || 0,
              'injured_free_agent_swap'
            )
          );

          transaction.set(requestRef, {
            rosterId: roster.id,
            rosterName: roster.name || '',
            requesterFactionId: faction.id,
            requesterFactionName: faction.name || '',
            requesterOwnerUid: getFactionOwnerUid(faction),
            requesterWrestlerId: ownWrestler.id,
            requesterWrestlerName: ownWrestler.name || '',
            targetFactionId: FREE_AGENT_TARGET,
            targetFactionName: 'Svincolati',
            targetOwnerUid: getFactionOwnerUid(faction),
            targetWrestlerId: targetWrestler.id,
            targetWrestlerName: targetWrestler.name || '',
            status: 'accepted',
            autoApproved: true,
            createdAt: serverTimestamp(),
            approvedAt: serverTimestamp(),
            approvedBy: getFactionOwnerUid(faction),
          });
        });

        setSelectedOwnWrestlerIds([]);
        setSelectedTargetFactionId('');
        setSelectedTargetWrestlerIds([]);
        setSwapRequestMessage('');
        setSuccessMessage('Sostituzione completata: il wrestler infortunato e stato rimpiazzato con uno svincolato.');
        await Promise.all([loadMarketData(), loadSwapRequests(roster.id)]);
        return;
      }

      const requestRef = await addDoc(collection(db, 'rosters', roster.id, 'swapRequest'), {
        rosterId: roster.id,
        rosterName: roster.name || '',
        requesterFactionId: faction.id,
        requesterFactionName: faction.name || '',
        requesterOwnerUid: getFactionOwnerUid(faction),
        requesterWrestlerId: ownWrestler.id,
        requesterWrestlerIds: requesterWrestlerIds,
        requesterWrestlerName: ownWrestler.name || '',
        requesterWrestlerNames: ownWrestlers.map((wrestler) => wrestler.name || ''),
        targetFactionId: targetFaction.id,
        targetFactionName: targetFaction.name || '',
        targetOwnerUid: getFactionOwnerUid(targetFaction),
        targetWrestlerId: targetWrestler.id,
        targetWrestlerIds: targetWrestlerIds,
        targetWrestlerName: targetWrestler.name || '',
        targetWrestlerNames: targetWrestlers.map((wrestler) => wrestler.name || ''),
        wrestlerCount: requesterWrestlerIds.length,
        requestMessage: normalizedRequestMessage,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      let notificationFeedback = '';
      try {
        const sendSwapRequestNotification = httpsCallable(functions, 'sendSwapRequestNotification');
        const notificationResult = await sendSwapRequestNotification({
          rosterId: roster.id,
          requestId: requestRef.id,
        });
        const notificationData = notificationResult?.data || {};
        if (Number(notificationData.success || 0) <= 0) {
          notificationFeedback =
            notificationData.message ||
            'Notifica non inviata al destinatario.';
        }
      } catch (notificationError) {
        console.warn('Notifica proposta scambio non inviata:', notificationError);
        notificationFeedback =
          notificationError?.message || 'Notifica non inviata al destinatario.';
      }

      setSelectedOwnWrestlerIds([]);
      setSelectedTargetFactionId('');
      setSelectedTargetWrestlerIds([]);
      setSwapRequestMessage('');
      const baseSuccessMessage =
        requesterWrestlerIds.length > 1
          ? `Richiesta multipla inviata: ${requesterWrestlerIds.length} wrestler proposti a ${targetFaction.name || 'un altra fazione'}.`
          : 'Richiesta di scambio inviata. Il giocatore della fazione avversaria potrà accettarla dal suo mercato.';
      setSuccessMessage(
        notificationFeedback
          ? `${baseSuccessMessage} Avviso notifica: ${notificationFeedback}`
          : baseSuccessMessage
      );
      await loadSwapRequests(roster.id);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError.message || 'Errore durante l invio della richiesta di scambio');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleAcceptSwapRequest = async (requestItem) => {
    if (!roster || !faction || !currentUser) return;

    if (requestItem.targetFactionId !== faction.id) {
      setError('Solo il destinatario della richiesta puo accettare questo scambio.');
      return;
    }

    if (isSectionBlocked) {
      setError('Mercato bloccato: non puoi accettare richieste in questo momento.');
      return;
    }

    setRequestActionId(requestItem.id);
    setError('');
    setSuccessMessage('');

    try {
      await runTransaction(db, async (transaction) => {
        const requesterWrestlerIds = getSwapRequestWrestlerIds(requestItem, 'requester');
        const targetWrestlerIds = getSwapRequestWrestlerIds(requestItem, 'target');

        if (
          requesterWrestlerIds.length === 0 ||
          targetWrestlerIds.length === 0 ||
          requesterWrestlerIds.length !== targetWrestlerIds.length
        ) {
          throw new Error('La richiesta non contiene un numero valido di wrestler per lo scambio.');
        }

        const requestRef = doc(db, 'rosters', roster.id, 'swapRequest', requestItem.id);
        const requesterFactionRef = doc(db, 'factions', requestItem.requesterFactionId);
        const targetFactionRef = doc(db, 'factions', requestItem.targetFactionId);
        const requesterWrestlerRefs = requesterWrestlerIds.map((wrestlerId) =>
          doc(db, 'wrestlers', wrestlerId)
        );
        const targetWrestlerRefs = targetWrestlerIds.map((wrestlerId) =>
          doc(db, 'wrestlers', wrestlerId)
        );

        const requestSnap = await transaction.get(requestRef);
        const requesterFactionSnap = await transaction.get(requesterFactionRef);
        const targetFactionSnap = await transaction.get(targetFactionRef);
        const requesterWrestlerSnaps = await Promise.all(
          requesterWrestlerRefs.map((requesterWrestlerRef) =>
            transaction.get(requesterWrestlerRef)
          )
        );
        const targetWrestlerSnaps = await Promise.all(
          targetWrestlerRefs.map((targetWrestlerRef) =>
            transaction.get(targetWrestlerRef)
          )
        );

        if (!requestSnap.exists()) throw new Error('Richiesta non trovata');
        if (requestSnap.data().status !== 'pending') throw new Error('La richiesta non e piu pendente');
        if (!requesterFactionSnap.exists() || !targetFactionSnap.exists()) throw new Error('Una delle fazioni non esiste piu');
        if (
          requesterWrestlerSnaps.some((requesterWrestlerSnap) => !requesterWrestlerSnap.exists()) ||
          targetWrestlerSnaps.some((targetWrestlerSnap) => !targetWrestlerSnap.exists())
        ) {
          throw new Error('Uno dei wrestler non esiste piu');
        }

        const requesterFaction = { id: requesterFactionSnap.id, ...requesterFactionSnap.data() };
        const targetFaction = { id: targetFactionSnap.id, ...targetFactionSnap.data() };

        if (getFactionOwnerUid(targetFaction) !== currentUser.uid) {
          throw new Error('Solo il proprietario della fazione destinataria puo accettare la richiesta');
        }

        const requesterStoredWrestlers = getFactionStoredWrestlers(requesterFaction);
        const targetStoredWrestlers = getFactionStoredWrestlers(targetFaction);
        const requesterEntriesById = requesterStoredWrestlers.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});
        const targetEntriesById = targetStoredWrestlers.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});

        if (
          requesterWrestlerIds.some((wrestlerId) => !requesterEntriesById[wrestlerId]) ||
          targetWrestlerIds.some((wrestlerId) => !targetEntriesById[wrestlerId])
        ) {
          throw new Error('Uno dei wrestler selezionati non appartiene piu alla fazione indicata');
        }

        const liveWrestlersById = { ...wrestlersById };
        requesterWrestlerIds.forEach((wrestlerId, index) => {
          liveWrestlersById[wrestlerId] = normalizeWrestlerData(
            wrestlerId,
            requesterWrestlerSnaps[index].data()
          );
        });
        targetWrestlerIds.forEach((wrestlerId, index) => {
          liveWrestlersById[wrestlerId] = normalizeWrestlerData(
            wrestlerId,
            targetWrestlerSnaps[index].data()
          );
        });

        const requesterMembers = buildFactionMembers(requesterFaction, liveWrestlersById);
        const targetMembers = buildFactionMembers(targetFaction, liveWrestlersById);
        const requesterGenderViolation = getFactionGenderMinimumViolationAfterSwap({
          roster,
          currentMembers: requesterMembers,
          outgoingIds: requesterWrestlerIds,
          incomingMembers: targetWrestlerIds.map((wrestlerId) => liveWrestlersById[wrestlerId]),
          messagePrefix: 'Questo scambio non e piu valido',
          subject: `la fazione ${requesterFaction.name || 'avversaria'}`,
        });
        if (requesterGenderViolation) {
          throw new Error(requesterGenderViolation);
        }

        const targetGenderViolation = getFactionGenderMinimumViolationAfterSwap({
          roster,
          currentMembers: targetMembers,
          outgoingIds: targetWrestlerIds,
          incomingMembers: requesterWrestlerIds.map((wrestlerId) => liveWrestlersById[wrestlerId]),
          messagePrefix: 'Non puoi accettare questo scambio',
          subject: 'la tua fazione',
        });
        if (targetGenderViolation) {
          throw new Error(targetGenderViolation);
        }

        const requesterReplacementMap = buildSwapReplacementMap(
          requesterWrestlerIds,
          targetWrestlerIds
        );
        const targetReplacementMap = buildSwapReplacementMap(
          targetWrestlerIds,
          requesterWrestlerIds
        );
        const requesterSwapSummariesById = {};
        const targetSwapSummariesById = {};

        requesterWrestlerIds.forEach((wrestlerId, index) => {
          const requesterEntry = requesterEntriesById[wrestlerId];
          const requesterWrestlerScore =
            requesterWrestlerSnaps[index].data().score || 0;

          requesterSwapSummariesById[wrestlerId] = getFactionMemberSwapSummary({
            faction: requesterFaction,
            roster,
            wrestlerId,
            currentScore: requesterWrestlerScore,
            startScore: requesterEntry.startScore || 0,
            accumulatedPoints: requesterEntry.accumulatedPoints || 0,
            status: requesterEntry.status || null,
          });
        });

        targetWrestlerIds.forEach((wrestlerId, index) => {
          const targetEntry = targetEntriesById[wrestlerId];
          const targetWrestlerScore = targetWrestlerSnaps[index].data().score || 0;

          targetSwapSummariesById[wrestlerId] = getFactionMemberSwapSummary({
            faction: targetFaction,
            roster,
            wrestlerId,
            currentScore: targetWrestlerScore,
            startScore: targetEntry.startScore || 0,
            accumulatedPoints: targetEntry.accumulatedPoints || 0,
            status: targetEntry.status || null,
          });
        });

        const requesterUpdatedWrestlers = requesterStoredWrestlers.map((item) => {
          const wrestlerInId = requesterReplacementMap[item.id];

          if (!wrestlerInId) return item;

          return {
            ...item,
            ...buildSwapPayload(
              item,
              wrestlerInId,
              liveWrestlersById[wrestlerInId]?.score || 0
            ),
            id: wrestlerInId,
            wrestlerId: wrestlerInId,
          };
        });
        const targetUpdatedWrestlers = targetStoredWrestlers.map((item) => {
          const wrestlerInId = targetReplacementMap[item.id];

          if (!wrestlerInId) return item;

          return {
            ...item,
            ...buildSwapPayload(
              item,
              wrestlerInId,
              liveWrestlersById[wrestlerInId]?.score || 0
            ),
            id: wrestlerInId,
            wrestlerId: wrestlerInId,
          };
        });
        const lineupUpdateRequester = buildFactionLineupUpdateAfterSwap({
          faction: requesterFaction,
          roster,
          updatedWrestlers: requesterUpdatedWrestlers,
          replacements: requesterReplacementMap,
          now: new Date(),
        });
        const lineupUpdateTarget = buildFactionLineupUpdateAfterSwap({
          faction: targetFaction,
          roster,
          updatedWrestlers: targetUpdatedWrestlers,
          replacements: targetReplacementMap,
          now: new Date(),
        });

        const requesterCurrentPoints = requesterWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (requesterSwapSummariesById[wrestlerId]?.currentPoints || 0),
          0
        );
        const requesterSavedPoints = requesterWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (requesterSwapSummariesById[wrestlerId]?.savedPoints || 0),
          0
        );
        const targetCurrentPoints = targetWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (targetSwapSummariesById[wrestlerId]?.currentPoints || 0),
          0
        );
        const targetSavedPoints = targetWrestlerIds.reduce(
          (sum, wrestlerId) =>
            sum + (targetSwapSummariesById[wrestlerId]?.savedPoints || 0),
          0
        );

        transaction.update(requesterFactionRef, {
          ...buildFactionMemberFields(requesterUpdatedWrestlers),
          ...(lineupUpdateRequester || {}),
          accumulatedPoints: (requesterFaction.accumulatedPoints || 0) + requesterCurrentPoints,
          sumPoints: (requesterFaction.sumPoints || 0) + requesterSavedPoints,
          updatedAt: serverTimestamp(),
        });
        transaction.update(targetFactionRef, {
          ...buildFactionMemberFields(targetUpdatedWrestlers),
          ...(lineupUpdateTarget || {}),
          accumulatedPoints: (targetFaction.accumulatedPoints || 0) + targetCurrentPoints,
          sumPoints: (targetFaction.sumPoints || 0) + targetSavedPoints,
          updatedAt: serverTimestamp(),
        });

        requesterWrestlerIds.forEach((wrestlerId, index) => {
          const requesterEntry = requesterEntriesById[wrestlerId];
          const targetWrestlerId = targetWrestlerIds[index];

          transaction.set(
            doc(collection(db, 'factions', requestItem.requesterFactionId, 'swaps')),
            buildSwapHistory(
              wrestlerId,
              targetWrestlerId,
              requesterSwapSummariesById[wrestlerId],
              requesterEntry.cost || 0
            )
          );
        });
        targetWrestlerIds.forEach((wrestlerId, index) => {
          const targetEntry = targetEntriesById[wrestlerId];
          const requesterWrestlerId = requesterWrestlerIds[index];

          transaction.set(
            doc(collection(db, 'factions', requestItem.targetFactionId, 'swaps')),
            buildSwapHistory(
              wrestlerId,
              requesterWrestlerId,
              targetSwapSummariesById[wrestlerId],
              targetEntry.cost || 0
            )
          );
        });

        transaction.update(requestRef, {
          status: 'accepted',
          approvedAt: serverTimestamp(),
          approvedBy: currentUser.uid,
        });
      });

      setSuccessMessage('Richiesta accettata: lo scambio e stato completato.');
      await Promise.all([loadMarketData(), loadSwapRequests(roster.id)]);
    } catch (acceptError) {
      console.error(acceptError);
      setError(acceptError.message || 'Errore durante l accettazione della richiesta.');
    } finally {
      setRequestActionId('');
    }
  };

  const handleRejectSwapRequest = async (requestItem) => {
    if (!roster || !faction || !currentUser) return;

    if (requestItem.targetFactionId !== faction.id) {
      setError('Solo il destinatario della richiesta puo rifiutare questo scambio.');
      return;
    }

    setRequestActionId(requestItem.id);
    setError('');
    setSuccessMessage('');

    try {
      await updateDoc(doc(db, 'rosters', roster.id, 'swapRequest', requestItem.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: currentUser.uid,
      });

      setSuccessMessage('Richiesta rifiutata.');
      await loadSwapRequests(roster.id);
    } catch (rejectError) {
      console.error(rejectError);
      setError('Errore durante il rifiuto della richiesta.');
    } finally {
      setRequestActionId('');
    }
  };

  const handleSaveMembers = async () => {
    if (!faction || !roster) return;

    if (roster.gameType === 'Competitivo') {
      setError('Usa le richieste di scambio del mercato competitivo');
      return;
    }

    if (autoBlocked) {
      setError('Impossibile effettuare scambi durante uno show in corso');
      return;
    }

    if (!swapsData.enabled) {
      setError('Gli scambi sono attualmente disabilitati');
      return;
    }

    if (priceDetails.newCredits < 0) {
      setError('Crediti insufficienti per completare lo scambio');
      return;
    }

    if (selectedToRemove.length !== selectedToAdd.length || selectedToRemove.length === 0) {
      setError('Seleziona lo stesso numero di wrestler in uscita e in entrata');
      return;
    }

    const incomingMembers = selectedToAdd
      .map((wrestlerId) => allWrestlers.find((wrestler) => wrestler.id === wrestlerId))
      .filter(Boolean);
    const genderViolation = getFactionGenderMinimumViolationAfterSwap({
      roster,
      currentMembers: members,
      outgoingIds: selectedToRemove,
      incomingMembers,
      messagePrefix: 'Non puoi confermare questo scambio',
      subject: 'la tua fazione',
    });

    if (genderViolation) {
      setError(genderViolation);
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      let newAccumulatedPoints = faction.accumulatedPoints || 0;
      let newSumPoints = faction.sumPoints || 0;
      const swapSummariesByWrestlerId = {};
      const swapReplacements = buildSwapReplacementMap(
        selectedToRemove,
        selectedToAdd
      );
      selectedToRemove.forEach((wrestlerId) => {
        const member = members.find((item) => item.id === wrestlerId);
        const wrestlerItem = wrestlersById[wrestlerId] || member;
        if (member && wrestlerItem) {
          const swapSummary = getFactionMemberSwapSummary({
            faction,
            roster,
            wrestlerId,
            currentScore: wrestlerItem.score || 0,
            startScore: member.startScore || 0,
            accumulatedPoints: member.accumulatedPoints || 0,
            status: member.status || null,
          });
          swapSummariesByWrestlerId[wrestlerId] = swapSummary;
          newAccumulatedPoints += swapSummary.currentPoints;
          newSumPoints += swapSummary.savedPoints;
        }
      });

      const updatedWrestlers = getFactionStoredWrestlers(faction).map((member) => {
        if (!swapReplacements[member.id]) {
          return member;
        }

        const wrestlerId = swapReplacements[member.id];
        const wrestlerItem =
          wrestlersById[wrestlerId] ||
          allWrestlers.find((item) => item.id === wrestlerId) ||
          {};

        return {
          ...member,
          id: wrestlerId,
          wrestlerId,
          cost: wrestlerItem.price || 0,
          startScore: wrestlerItem.score || 0,
          accumulatedPoints: 0,
          status: member.status || null,
        };
      });
      const lineupUpdate = buildFactionLineupUpdateAfterSwap({
        faction,
        roster,
        updatedWrestlers,
        replacements: swapReplacements,
        now: new Date(),
      });

      await updateDoc(doc(db, 'factions', faction.id), {
        ...buildFactionMemberFields(updatedWrestlers),
        ...(lineupUpdate || {}),
        accumulatedPoints: newAccumulatedPoints,
        sumPoints: newSumPoints,
        creditsLeft: priceDetails.newCredits,
        swapsUsed: swapCount + (isWithinFirstDay ? 0 : selectedToRemove.length),
        updatedAt: serverTimestamp(),
      });

      if (!isWithinFirstDay) {
        await addDoc(collection(db, 'factions', faction.id, 'swaps'), {
          date: serverTimestamp(),
          swaps: selectedToRemove.map((wrestlerId, index) => {
            const addedId = selectedToAdd[index];
            const member = members.find((item) => item.id === wrestlerId) || {};
            const wrestlerIn = wrestlersById[addedId] || allWrestlers.find((item) => item.id === addedId) || {};
            const swapSummary = swapSummariesByWrestlerId[wrestlerId] || {
              currentPoints: 0,
              totalPoints: 0,
              savedPoints: 0,
            };

            return {
              wrestlerIn: doc(db, 'wrestlers', addedId),
              wrestlerOut: doc(db, 'wrestlers', wrestlerId),
              priceIn: wrestlerIn.price || 0,
              priceOut: String(member.cost || 0),
              scoreOut: swapSummary.savedPoints,
              currentPointsOut: swapSummary.currentPoints,
              accumulatedPointsOut: member.accumulatedPoints || 0,
              totalPointsOut: swapSummary.totalPoints,
              sumPointsOut: swapSummary.savedPoints,
              wrestlerInName: wrestlerIn.name,
              wrestlerOutName: (wrestlersById[wrestlerId] || member).name,
            };
          }),
        });
      }

      setSuccessMessage('Mercato aggiornato con successo');
      await loadMarketData();
    } catch (saveError) {
      console.error(saveError);
      setError('Errore durante il salvataggio del mercato');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading || authLoading) return <LoadingSpinner fullPage />;

  if (!currentUser) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          Devi effettuare l accesso per gestire il mercato della fazione.
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/auth')}>
          Vai al login
        </button>
      </div>
    );
  }

  if (error && !faction) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/profile')}>
          Torna al profilo
        </button>
      </div>
    );
  }

  const blockedTitle = autoBlocked ? 'Mercato temporaneamente bloccato' : 'Mercato disattivato';
  const blockedDescription = autoBlocked
    ? `C'e uno show in corso. Mercato e lineup restano bloccati fino alle ${currentShow?.endTime?.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })}.`
    : 'Mercato e lineup sono bloccati dalle impostazioni generali degli scambi.';
  const competitiveBlockedStats = [
    {
      label: 'La tua fazione',
      value: faction?.name || 'N/D',
      helper: `${members.length} wrestler in fazione`,
    },
    {
      label: 'Richieste pendenti',
      value: pendingFactionRequests.length,
      helper: `${incomingPendingRequests.length} da gestire`,
    },
  ];
  const rosterBlockedStats = [
    {
      label: 'Scambi usati',
      value: `${swapCount}/${maxSwaps}`,
      helper: 'Limite disponibile per questa fazione',
    },
    {
      label: 'Crediti residui',
      value: faction?.creditsLeft ?? 0,
      helper: 'Budget fermo durante il blocco',
    },
  ];

  return (
    <div className="profile-page-shell">
      <div className="container py-4 py-lg-5">

        {successMessage && (
          <div className="alert alert-success border-0 shadow-sm rounded-4 mb-4">
            {successMessage}
          </div>
        )}

        {error && faction && (
          <div className="alert alert-danger border-0 shadow-sm rounded-4 mb-4">
            {error}
          </div>
        )}

        {isCompetitive ? (
          <>
            <div className="row g-4">
              <div className="col-12 col-xl-7">
                <section className="profile-panel-card h-100">
                  <div className="profile-panel-head">
                    <div>
                      <h4 className="brand-title mb-1 text-center">Mercato</h4>
                      <p className="text-muted-brand mb-0 text-center">
                        Qui puoi effettuare scambi con altre fazioni o sostituire un tuo wrestler infortunato con uno svincolato.
                      </p>
                    </div>
                  </div>

                  <div className="mt-3">
                    {isSectionBlocked ? (
                      <FactionBlockedState
                        title={blockedTitle}
                        description={blockedDescription}
                        autoBlocked={autoBlocked}
                        currentShow={currentShow}
                        blockedAt={swapsData.blockedAt}
                        stats={competitiveBlockedStats}
                      />
                    ) : (
                      <>
                        {/*{hasInjuredMembers && (
                          <div className="mb-4 text-center">
                            <span className="profile-alert-badge p-2 fs-6 text-center">{competitiveRecommendationText}</span>
                          </div>
                        )}*/}
                        {!isPremium && renderMarketPremiumPreview()}
                        {isPremium && (
                          displayedPremiumTradeSuggestions.length > 0 ||
                          premiumRemovalSuggestions.length > 0 ||
                          premiumAuctionSuggestions.length > 0
                        ) && (
                          <div className="market-choice-card premium-trade-helper mb-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <div className="fw-bold text-light">Aiuto scambi</div>
                                <PremiumInfoButton
                                  title="Come funziona il trend"
                                  ariaLabel="Spiegazione del trend premium"
                                  buttonClassName="premium-info-button--inline"
                                  dialogClassName="premium-info-dialog--trend"
                                  bodyClassName="premium-info-dialog__body--trend"
                                  iconClassName="bi-info-circle"
                                >
                                  <div className="premium-trend-modal">
                                    <div className="premium-comment-callout premium-trend-modal__intro">
                                      <small>Panoramica</small>
                                      <p>
                                        Il trend ti aiuta a capire il momento di un wrestler
                                        incrociando rendimento recente e quotazione. I
                                        suggerimenti premium usano questi dati per evidenziare
                                        chi sta salendo e chi invece sta perdendo spinta.
                                      </p>
                                    </div>

                                    <div className="premium-trend-modal__section">
                                      <div className="premium-trend-modal__section-header">
                                        <small>1. Calcolo del trend</small>
                                        <strong>Come vengono letti punti e prezzo</strong>
                                      </div>

                                      <div className="premium-trend-modal__grid">
                                        <div className="premium-trend-modal__card">
                                          <small>Trend punti</small>
                                          <strong className="premium-trend-modal__formula">
                                            Ultime 3 partecipazioni - 3 precedenti
                                          </strong>
                                          <p>
                                            Se il valore sale, il wrestler sta portando piu
                                            punti di prima. Se scende, il rendimento recente e
                                            peggiorato.
                                          </p>
                                        </div>

                                        <div className="premium-trend-modal__card">
                                          <small>Trend prezzo</small>
                                          <strong className="premium-trend-modal__formula">
                                            Ultima quotazione - quotazione precedente
                                          </strong>
                                          <p>
                                            Misura se il mercato lo sta rivalutando oppure no.
                                            Un numero positivo indica crescita di quotazione.
                                          </p>
                                        </div>

                                        <div className="premium-trend-modal__card">
                                          <small>Ordine &quot;Da chiedere&quot;</small>
                                          <strong className="premium-trend-modal__formula">
                                            Trend punti x2 + Trend prezzo x3 + Punti x0.05
                                          </strong>
                                          <p>
                                            Il prezzo pesa di piu, poi il trend punti, mentre
                                            il punteggio totale serve come rifinitura finale.
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="premium-trend-modal__section">
                                      <div className="premium-trend-modal__section-header">
                                        <small>2. Ordine dei suggerimenti</small>
                                        <strong>Come leggere i due blocchi del mercato</strong>
                                      </div>

                                      <div className="premium-trend-modal__grid premium-trend-modal__grid--two">
                                        <div className="premium-trend-modal__card premium-trend-modal__card--accent">
                                          <small>Da chiedere</small>
                                          <strong>Preferiti, trend e score</strong>
                                          <p>
                                            Vengono favoriti i wrestler che stanno crescendo o
                                            hanno segnali positivi. I preferiti hanno priorita a
                                            parita di profilo.
                                          </p>
                                        </div>

                                        <div className="premium-trend-modal__card premium-trend-modal__card--accent">
                                          <small>Da valutare in uscita</small>
                                          <strong>Non attivi, infortunati e rendimento basso</strong>
                                          <p>
                                            Qui salgono i wrestler non attivi, poi gli
                                            infortunati e infine quelli con meno punti recenti o
                                            trend negativo.
                                          </p>
                                        </div>

                                        {isCompetitive && (
                                          <div className="premium-trend-modal__card premium-trend-modal__card--accent">
                                            <small>Suggerimenti prossima asta</small>
                                            <strong>Svincolati con trend utile da seguire</strong>
                                            <p>
                                              Nel competitivo gli svincolati vengono mostrati a
                                              parte, cosi puoi prepararli per la prossima asta
                                              senza confonderli con gli scambi tra fazioni.
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </PremiumInfoButton>
                              </div>
                              <PremiumFeaturePill label="Premium" />
                            </div>
                            {premiumRemovalSuggestions.length > 0 && (
                              <div className="mb-3">
                                <div className="premium-trade-helper__section-title">
                                  Da valutare in uscita
                                </div>
                                <div className="premium-trade-helper__list">
                                  {premiumRemovalSuggestions.map((suggestion) => (
                                    <button
                                      key={`remove-${suggestion.id}`}
                                      type="button"
                                      className="premium-trade-helper__item premium-trade-helper__item--remove"
                                      onClick={() => handleUsePremiumRemovalSuggestion(suggestion)}
                                      disabled={requestSubmitting}
                                    >
                                      <span className="premium-trade-helper__main">
                                        <strong>{suggestion.name || 'Wrestler'}</strong>
                                        <small
                                          className={getPremiumPositionClassName(
                                            suggestion.position
                                          )}
                                        >
                                          {getPremiumPositionLabel(suggestion.position)}
                                        </small>
                                      </span>
                                      <span className="premium-trade-helper__meta">
                                        {suggestion.active === false && (
                                          <span className="text-warning">
                                            Non attivo
                                          </span>
                                        )}
                                        <span>{suggestion.recentPoints} pt recenti</span>
                                        <span className={suggestion.scoreTrend < 0 ? 'text-danger' : ''}>
                                          {suggestion.scoreTrend > 0 ? `+${suggestion.scoreTrend}` : suggestion.scoreTrend} trend pt
                                        </span>
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {displayedPremiumTradeSuggestions.length > 0 && (
                              <div>
                                <div className="premium-trade-helper__section-title">
                                  Da chiedere
                                </div>
                                <div className="premium-trade-helper__list">
                                  {displayedPremiumTradeSuggestions.map((suggestion) => (
                                    <button
                                      key={`${suggestion.sourceType}-${suggestion.sourceFactionId || 'free'}-${suggestion.id}`}
                                      type="button"
                                      className="premium-trade-helper__item"
                                      onClick={() => handleUsePremiumSuggestion(suggestion)}
                                      disabled={requestSubmitting}
                                    >
                                      <span className="premium-trade-helper__main">
                                        <strong>{suggestion.name || 'Wrestler'}</strong>
                                        <small>{suggestion.sourceName}</small>
                                      </span>
                                      <span className="premium-trade-helper__meta">
                                        {suggestion.isFavorite && (
                                          <span className="premium-trade-helper__badge">
                                            <i className="bi bi-star-fill"></i>
                                          </span>
                                        )}
                                        <span>
                                          {suggestion.scoreTrend > 0 ? `+${suggestion.scoreTrend}` : suggestion.scoreTrend || 0} pt
                                        </span>
                                        <span className={suggestion.priceTrend > 0 ? 'text-success' : ''}>
                                          {suggestion.priceTrend > 0 ? `+${suggestion.priceTrend}` : suggestion.priceTrend || 0}
                                          <CreditIcon size={14} />
                                        </span>
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {isCompetitive && premiumAuctionSuggestions.length > 0 && (
                              <div className={displayedPremiumTradeSuggestions.length > 0 ? "mt-3" : ""}>
                                <div className="premium-trade-helper__section-title">
                                  Suggerimenti prossima asta
                                </div>
                                <div className="premium-trade-helper__list">
                                  {premiumAuctionSuggestions.map((suggestion) => (
                                    <button
                                      key={`auction-${suggestion.id}`}
                                      type="button"
                                      className="premium-trade-helper__item"
                                      onClick={() => handleUsePremiumSuggestion(suggestion)}
                                      disabled={requestSubmitting}
                                    >
                                      <span className="premium-trade-helper__main">
                                        <strong>{suggestion.name || 'Wrestler'}</strong>
                                      </span>
                                      <span className="premium-trade-helper__meta">
                                        {suggestion.isFavorite && (
                                          <span className="premium-trade-helper__badge">
                                            <i className="bi bi-star-fill"></i>
                                          </span>
                                        )}
                                        <span>
                                          {suggestion.scoreTrend > 0 ? `+${suggestion.scoreTrend}` : suggestion.scoreTrend || 0} pt
                                        </span>
                                        <span className={suggestion.priceTrend > 0 ? 'text-success' : ''}>
                                          {suggestion.priceTrend > 0 ? `+${suggestion.priceTrend}` : suggestion.priceTrend || 0}
                                          <CreditIcon size={14} />
                                        </span>
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="row g-3">
                          <div className="col-12 col-lg-6">
                            <div className="market-choice-card market-choice-card--step h-100">
                              <div className="market-choice-card__step-head">
                                <div className="market-choice-card__step-copy">
                                  <span className="market-choice-card__step-index">1</span>
                                  <div>
                                    <label className="form-label fw-bold text-light mb-1">Scegli i wrestler da cedere</label>
                                    <small className="text-muted-brand d-block">
                                      Seleziona uno o piu wrestler della tua fazione.
                                    </small>
                                  </div>
                                </div>
                                <span className="badge rounded-pill text-bg-dark">
                                  {selectedOwnWrestlerIds.length} selezionati
                                </span>
                              </div>
                              <div
                                className="market-selection-list"
                                role="listbox"
                                aria-label="Scegli i wrestler da cedere"
                                aria-multiselectable="true"
                              >
                                {sortedOwnMembers.map((member) => (
                                  <button
                                    key={member.id}
                                    type="button"
                                    className={`market-selection-item ${
                                      selectedOwnWrestlerIds.includes(member.id)
                                        ? 'is-selected'
                                        : ''
                                    }`}
                                    onClick={() => toggleOwnWrestlerSelection(member.id)}
                                    disabled={requestSubmitting}
                                    aria-pressed={selectedOwnWrestlerIds.includes(member.id)}
                                    aria-label={`${member.name}${
                                      member.isInjured ? ', infortunato' : ''
                                    }${
                                      member.active === false ? ', non attivo' : ''
                                    }${
                                      selectedOwnWrestlerIds.includes(member.id)
                                        ? ', selezionato'
                                        : ''
                                    }`}
                                  >
                                    <span className="market-selection-item__main">
                                      <span className="market-selection-item__name">
                                        {member.name}
                                      </span>
                                    </span>
                                    <span className="market-selection-item__aside">
                                      {member.isInjured && (
                                        <InjuryIcon
                                          size={24}
                                          iconSize="0.72rem"
                                          className="market-selection-item__injury"
                                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.22)' }}
                                          title="Wrestler infortunato"
                                        />
                                      )}
                                      {member.active === false && (
                                        <span
                                          className="market-selection-status market-selection-status--inactive"
                                          title="Wrestler non attivo"
                                          aria-label="Wrestler non attivo"
                                        >
                                          <i className="bi bi-ban" aria-hidden="true"></i>
                                        </span>
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

{/*

                          <div className="col-12 col-lg-5">
                            <div className="market-choice-card h-100">
                              <label className="form-label fw-bold text-light">Come vuoi sostituirlo?</label>
                              <div className="market-mode-switch">
                                <button
                                  type="button"
                                  className={`market-mode-option ${swapTargetMode === 'faction' ? 'active' : ''}`}
                                  onClick={() => setSwapTargetMode('faction')}
                                  disabled={requestSubmitting}
                                >
                                  <span className="market-mode-option__title">Altra fazione</span>
                                  <small>Proponi lo scambio ad un altro giocatore</small>
                                </button>
                                <button
                                  type="button"
                                  className={`market-mode-option ${swapTargetMode === 'free_agent' ? 'active' : ''} ${selectedOwnWrestler?.isInjured ? 'border-2 is-recommended' : ''}`}
                                  onClick={() => {
                                    if (selectedOwnWrestler?.isInjured) {
                                      setSwapTargetMode('free_agent');
                                    }
                                  }}
                                  disabled={requestSubmitting || !selectedOwnWrestler?.isInjured}
                                >
                                  <span className="market-mode-option__title">Svincolato</span>
                                  <small>{selectedOwnWrestler?.isInjured ? 'Sostituzione immediata disponibile' : 'Solo per wrestler infortunati'}</small>
                                </button>
                              </div>
                            </div>
                          </div>
*/}

                          <div className="col-12">
                            <div className="market-choice-card market-choice-card--step">
                              <div className="market-choice-card__step-head mb-3">
                                <div className="market-choice-card__step-copy">
                                  <span className="market-choice-card__step-index">2</span>
                                  <div>
                                    <div className="fw-bold text-light">Scegli cosa chiedere</div>
                                    <small className="text-muted-brand d-block">
                                      Seleziona la fazione avversaria e poi i wrestler da richiedere.
                                    </small>
                                  </div>
                                </div>
                                <span className="badge rounded-pill text-bg-dark">
                                  {selectedTargetWrestlerIds.length} selezionati
                                </span>
                              </div>
                              <div className="row g-3">
                                {!isFreeAgentRequest && (
                                  <div className="col-12 col-lg-4">
                                    <label className="form-label fw-bold text-light">Seleziona la fazione avversaria</label>
                                    <select
                                      className="form-select market-dark-select"
                                      value={selectedTargetFactionId}
                                      onChange={(event) => setSelectedTargetFactionId(event.target.value)}
                                      disabled={requestSubmitting}
                                    >
                                      <option value="">Scegli una fazione</option>
                                      {targetFactions.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                      ))}
                                    </select>
                                    <small className="text-muted-brand d-block mt-2">
                                      Una richiesta multipla puo coinvolgere una sola fazione avversaria per volta.
                                    </small>
                                  </div>
                                )}

                                <div className={isFreeAgentRequest ? 'col-12' : 'col-12 col-lg-8'}>
                                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                    <label className="form-label fw-bold text-light mb-0">
                                      {isFreeAgentRequest ? 'Seleziona lo svincolato' : 'Seleziona i wrestler richiesti'}
                                    </label>
                                    {selectedTargetFactionName && (
                                      <span className="market-choice-card__step-pill">
                                        {selectedTargetFactionName}
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className="market-selection-list market-selection-list--comfortable"
                                    role="listbox"
                                    aria-label={isFreeAgentRequest ? 'Seleziona lo svincolato' : 'Seleziona i wrestler richiesti'}
                                    aria-multiselectable="true"
                                  >
                                    {targetMembers.length > 0 ? (
                                      targetMembers.map((member) => (
                                        <button
                                          key={member.id}
                                          type="button"
                                          className={`market-selection-item ${
                                            selectedTargetWrestlerIds.includes(member.id)
                                              ? 'is-selected'
                                              : ''
                                          }`}
                                          onClick={() => toggleTargetWrestlerSelection(member.id)}
                                          disabled={(
                                            isFreeAgentRequest
                                              ? !selectedOwnWrestler?.isInjured || !freeAgentMembers.length
                                              : !selectedTargetFactionId
                                          ) || requestSubmitting}
                                          aria-pressed={selectedTargetWrestlerIds.includes(member.id)}
                                        >
                                          <span className="market-selection-item__main">
                                            <span className="market-selection-item__name">
                                              {member.name}
                                            </span>
                                          </span>
                                          <span className="market-selection-item__aside">
                                            {member.isInjured && (
                                              <InjuryIcon
                                                size={24}
                                                iconSize="0.72rem"
                                                className="market-selection-item__injury"
                                                style={{ backgroundColor: 'rgba(0, 0, 0, 0.22)' }}
                                                title="Wrestler infortunato"
                                              />
                                            )}
                                            {member.active === false && (
                                              <span
                                                className="market-selection-status market-selection-status--inactive"
                                                title="Wrestler non attivo"
                                                aria-label="Wrestler non attivo"
                                              >
                                                <i className="bi bi-ban" aria-hidden="true"></i>
                                              </span>
                                            )}
                                          </span>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="market-selection-empty">
                                        {!selectedTargetFactionId && !isFreeAgentRequest
                                          ? 'Scegli prima una fazione avversaria per vedere i wrestler disponibili.'
                                          : 'Nessun wrestler disponibile per questa selezione.'}
                                      </div>
                                    )}
                                  </div>
                                  <small className={`d-block mt-2 ${isFreeAgentRequest && !selectedOwnWrestler?.isInjured ? 'text-danger' : 'text-muted-brand'}`}>
                                    {isFreeAgentRequest
                                      ? selectedOwnWrestler?.isInjured
                                        ? 'La sostituzione verra confermata subito appena inviata.'
                                        : 'Per scegliere uno svincolato devi prima selezionare un wrestler infortunato.'
                                      : 'Scegli uno o piu wrestler della fazione selezionata. Tutti devono appartenere alla stessa fazione avversaria.'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="market-choice-card market-choice-card--step">
                              <div className="market-choice-card__step-head mb-3">
                                <div className="market-choice-card__step-copy">
                                  <span className="market-choice-card__step-index">3</span>
                                  <div>
                                    <div className="fw-bold text-light">Riepilogo proposta</div>
                                    <small className="text-muted-brand">
                                      Ogni riga rappresenta una coppia di scambio nello stesso ordine mostrato nelle liste.
                                    </small>
                                  </div>
                                </div>
                              </div>

                              {selectedSwapPairs.length > 0 ? (
                                <div className="market-request-pairs">
                                  {selectedSwapPairs.map((pair, index) => (
                                    <div
                                      key={`${pair.ownMember?.id || 'none'}-${pair.targetMember?.id || 'none'}-${index}`}
                                      className="market-request-pair"
                                    >
                                      <span className="market-request-pair__index">{index + 1}</span>
                                      <div className="market-request-pair__copy">
                                        <strong>{pair.ownMember?.name || 'Da selezionare'}</strong>
                                        <small>
                                          <i className="bi bi-arrow-left-right me-1"></i>
                                          {pair.targetMember?.name || 'Da selezionare'}
                                        </small>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="market-multi-select-empty">
                                  Seleziona i wrestler di entrambe le fazioni per costruire la proposta di scambio.
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFreeAgentRequest && (
                            <div className="col-12">
                              <div className="market-choice-card market-choice-card--step">
                                <div className="market-choice-card__step-head">
                                  <div className="market-choice-card__step-copy">
                                    <span className="market-choice-card__step-index">4</span>
                                    <div className="fw-bold text-light">Messaggio per il destinatario</div>
                                  </div>
                                  <small className="text-muted-brand">
                                    {swapRequestMessage.length}/{SWAP_REQUEST_MESSAGE_MAX_LENGTH}
                                  </small>
                                </div>
                                <textarea
                                  className="form-control market-dark-select"
                                  rows={3}
                                  maxLength={SWAP_REQUEST_MESSAGE_MAX_LENGTH}
                                  placeholder="Scrivi un messaggio facoltativo da mostrare nella notifica e nella pagina richieste."
                                  value={swapRequestMessage}
                                  onChange={(event) =>
                                    setSwapRequestMessage(
                                      event.target.value.slice(0, SWAP_REQUEST_MESSAGE_MAX_LENGTH)
                                    )
                                  }
                                  disabled={requestSubmitting}
                                />
                                <small className="text-muted-brand d-block mt-2">
                                  Il messaggio comparira nella push del giocatore avversario e nello storico della richiesta.
                                </small>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="d-flex justify-content-end mt-4 market-request-submit">
                          <button
                            type="button"
                            className="btn btn-brand-secondary rounded-pill px-4 d-inline-flex align-items-center gap-2"
                            onClick={handleCreateSwapRequest}
                            disabled={
                              requestSubmitting ||
                              isSectionBlocked ||
                              selectedOwnWrestlerIds.length === 0 ||
                              !selectedTargetFactionId ||
                              selectedTargetWrestlerIds.length === 0 ||
                              selectedOwnWrestlerIds.length !== selectedTargetWrestlerIds.length ||
                              hasPendingConflict
                            }
                          >
                            {requestSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm" />
                                Invio...
                              </>
                            ) : (
                              <>
                                <i className={`bi ${canAutoApproveFreeAgentSwap ? 'bi-lightning-charge-fill' : 'bi-send'}`} />
                                {canAutoApproveFreeAgentSwap ? 'Conferma sostituzione' : 'Invia richiesta'}
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </section>
              </div>

              <div className="col-12 col-xl-5">
                <section className="profile-panel-card h-100">
                  <div className="profile-panel-head">
                    <div>
                      <span className="profile-panel-eyebrow">Timeline</span>
                      <h4 className="brand-title mb-1">Storico richieste</h4>
                      <p className="text-muted-brand mb-0">Tutte le richieste che coinvolgono la tua fazione.</p>
                    </div>
                  </div>

                  <div className="mt-3">
                    {factionSwapRequests.length === 0 ? (
                      <div className="profile-empty-state compact">
                        <i className="bi bi-inboxes text-brand-secondary fs-2 d-block mb-2" />
                        <p className="text-muted-brand mb-0">Nessuna richiesta di scambio presente.</p>
                      </div>
                    ) : (
                      <div className="d-grid gap-3">
                        {factionSwapRequests.map((item) => {
                          const badge = getRequestBadge(item);
                          const requestPairings = buildSwapRequestPairings(item);
                          const pairCount =
                            requestPairings.filter(
                              (pair) => pair.requesterId && pair.targetId
                            ).length || requestPairings.length;
                          const requesterPreview = getSwapRequestPreviewLabel(
                            item,
                            'requester'
                          );
                          const targetPreview = getSwapRequestPreviewLabel(
                            item,
                            'target'
                          );
                          const requestMessage = normalizeString(item.requestMessage);

                          return (
                            <div key={item.id} className="profile-inline-empty">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <div className="fw-bold text-white">
                                    {requesterPreview} {'<->'} {targetPreview}
                                  </div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                    <small className="text-muted-brand">
                                      {item.autoApproved
                                        ? 'Sostituzione immediata con svincolato'
                                        : item.requesterFactionId === faction?.id
                                        ? `A ${item.targetFactionName}`
                                        : `Da ${item.requesterFactionName}`}
                                    </small>
                                    {pairCount > 1 && (
                                      <span className="badge rounded-pill text-bg-dark">
                                        {pairCount} wrestler
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span className={`badge ${badge.className}`}>{badge.label}</span>
                              </div>

                              {pairCount > 1 && (
                                <div className="market-request-summary__pairings mt-3">
                                  <div className="market-request-pairs">
                                    {requestPairings.map((pair, index) => (
                                      <div
                                        key={`${pair.requesterId || 'none'}-${pair.targetId || 'none'}-${index}`}
                                        className="market-request-pair"
                                      >
                                        <span className="market-request-pair__index">
                                          {index + 1}
                                        </span>
                                        <div className="market-request-pair__copy">
                                          <strong>{pair.requesterName}</strong>
                                          <small>
                                            <i className="bi bi-arrow-left-right me-1" />
                                            {pair.targetName}
                                          </small>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {requestMessage && (
                                <div className="mt-3 p-3 rounded border border-secondary bg-dark bg-opacity-25">
                                  <small className="text-muted-brand d-block mb-1">
                                    Messaggio allegato
                                  </small>
                                  <div
                                    className="small text-light"
                                    style={{ whiteSpace: 'pre-line' }}
                                  >
                                    {requestMessage}
                                  </div>
                                </div>
                              )}

                              <small className="text-muted-brand d-block mt-2">
                                {item.createdAt?.toDate
                                  ? item.createdAt.toDate().toLocaleString('it-IT')
                                  : 'Data non disponibile'}
                              </small>

                              {item.status === 'pending' &&
                                item.targetFactionId === faction?.id &&
                                !item.autoApproved && (
                                  <div className="d-flex flex-wrap justify-content-between mt-3 gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                      onClick={() => handleRejectSwapRequest(item)}
                                      disabled={requestActionId === item.id}
                                    >
                                      <i className="bi bi-x-circle me-1" />
                                      Rifiuta
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-brand-secondary rounded-pill px-3"
                                      onClick={() => handleAcceptSwapRequest(item)}
                                      disabled={requestActionId === item.id || isSectionBlocked}
                                    >
                                      {requestActionId === item.id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" />
                                          Accetto...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-check2-circle me-1" />
                                          Accetta
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>

            <div className="mt-4">
              <WrestlerAvailabilityList
                wrestlers={sortedAllWrestlers}
                factions={rosterFactions}
                title={roster?.gameType === 'Indipendente' ? 'Wrestler del roster' : 'Wrestler svincolati'}
                defaultExpanded={false}
                showFilters
                includeAssigned={roster?.gameType === 'Indipendente'}
                onlyMainRoster={Boolean(roster?.onlyMainRoster)}
                onlyNXT={Boolean(roster?.onlyNXT)}
              />
            </div>
          </>
        ) : (
          <div className="border-0 shadow-sm">
            <div className="card-body">
              {isSectionBlocked ? (
                <FactionBlockedState
                  title={blockedTitle}
                  description={blockedDescription}
                  autoBlocked={autoBlocked}
                  currentShow={currentShow}
                  blockedAt={swapsData.blockedAt}
                  stats={rosterBlockedStats}
                />
              ) : (
                <EditFactionFormWrestler
                  faction={faction}
                  roster={roster}
                  members={members}
                  allWrestlers={sortedAllWrestlers}
                  favoriteWrestlerIds={favoriteWrestlerIds}
                  selectedToRemove={selectedToRemove}
                  selectedToAdd={selectedToAdd}
                  swapCount={swapCount}
                  maxSwaps={maxSwaps}
                  isWithinFirstDay={isWithinFirstDay}
                  freeSwapEndDate={freeSwapEndDate}
                  canEditMembers={canEditMembers}
                  autoBlocked={autoBlocked}
                  swapBlockData={swapsData}
                  priceDetails={priceDetails}
                  remainingBudget={remainingBudget}
                  currentShow={currentShow}
                  onToggleRemove={handleToggleRemove}
                  onToggleAdd={handleToggleAdd}
                  onSaveMembers={handleSaveMembers}
                  isSaving={isSaving}
                  error={error}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

